import { useEffect, useState, useRef } from 'react';
import { useViewModel } from "mvvm/View";
import { QuestionViewModel } from "../ViewModels/QuestionViewModel";
import TextArea from "antd/es/input/TextArea";
import { ArrowUpOutlined } from '@ant-design/icons';
import { useDarkMode, useThemeColors, useAppSelector, useIsReadOnly } from "redux/hooks";
import { notification, Select, Modal } from "antd";

export function SQLInputView(props: {
    model: QuestionViewModel;
    hasSelectedJob: boolean;
    isAnimating?: boolean;
    editingJobID?: string;
    initialValues?: { sqlQueries: string[], sizes: number[], dimensions: number, title: string };
}) {
    const model = useViewModel(props.model);
    const { isAnimating = false } = props;
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const isReadOnly = useIsReadOnly();

    const [gridRows, setGridRows] = useState<number>(1);
    const [gridCols, setGridCols] = useState<number>(1);
    const [dashboardTitle, setDashboardTitle] = useState<string>('');
    const [cellQueries, setCellQueries] = useState<string[][]>([['']]);
    const [cellSpans, setCellSpans] = useState<{ rowSpan: number; colSpan: number }[][]>([[{ rowSpan: 1, colSpan: 1 }]]);
    const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
    const [isCellEditorOpen, setIsCellEditorOpen] = useState(false);
    const [cellMode, setCellMode] = useState<'saved' | 'manual'>('manual');
    const [cellSelectedName, setCellSelectedName] = useState<string | undefined>(undefined);
    const [cellText, setCellText] = useState<string>('');
    const hasInitialized = useRef(false);
    const lastModelEmptyState = useRef<boolean | null>(null);
    const lastEditingJobID = useRef<string | undefined>(undefined);

    const savedQueries = useAppSelector(state => state.user.settings?.queries || {});

    // Keep cellQueries and cellSpans in sync with grid size
    useEffect(() => {
        setCellQueries(prev => {
            const rows = gridRows;
            const cols = gridCols;
            const next: string[][] = [];
            for (let r = 0; r < rows; r++) {
                next[r] = [];
                for (let c = 0; c < cols; c++) {
                    next[r][c] = prev[r]?.[c] ?? '';
                }
            }
            return next;
        });
        setCellSpans(prev => {
            const rows = gridRows;
            const cols = gridCols;
            const next: { rowSpan: number; colSpan: number }[][] = [];
            for (let r = 0; r < rows; r++) {
                next[r] = [];
                for (let c = 0; c < cols; c++) {
                    const existing = prev[r]?.[c] ?? { rowSpan: 1, colSpan: 1 };
                    const maxRowSpan = Math.max(1, rows - r);
                    const maxColSpan = Math.max(1, cols - c);
                    next[r][c] = {
                        rowSpan: Math.min(existing.rowSpan, maxRowSpan),
                        colSpan: Math.min(existing.colSpan, maxColSpan)
                    };
                }
            }
            return next;
        });
    }, [gridRows, gridCols]);

    // Initialize from initialValues (edit mode) or model
    useEffect(() => {
        // Reset initialization if editingJobID changed
        const editingJobIDChanged = props.editingJobID !== lastEditingJobID.current;

        if (editingJobIDChanged) {
            hasInitialized.current = false;
            lastEditingJobID.current = props.editingJobID;
        }

        // Initialize if not initialized yet, OR if we have new initialValues for edit mode
        const shouldInitialize = !hasInitialized.current || (props.initialValues && props.editingJobID && editingJobIDChanged);

        if (shouldInitialize) {
            if (props.initialValues && props.editingJobID) {
                // Edit mode: initialize from initialValues
                const { sqlQueries, sizes, dimensions, title } = props.initialValues;

                // Set dashboard title
                setDashboardTitle(title);

                // Parse dimensions to get grid rows and cols (e.g., 34 -> 3 rows, 4 cols)
                // Allow any configuration as long as rows × cols <= 24
                const dimStr = dimensions.toString();
                const parsedRows = dimStr.length >= 2 ? parseInt(dimStr[0]) || 1 : 1;
                const parsedCols = dimStr.length >= 2 ? parseInt(dimStr[1]) || 1 : 1;
                // Clamp to ensure product doesn't exceed 24
                let finalRows = Math.max(1, parsedRows);
                let finalCols = Math.max(1, parsedCols);
                if (finalRows * finalCols > 24) {
                    // If product exceeds 24, reduce to fit
                    if (finalRows > finalCols) {
                        finalRows = Math.floor(24 / finalCols);
                    } else {
                        finalCols = Math.floor(24 / finalRows);
                    }
                }
                setGridRows(finalRows);
                setGridCols(finalCols);

                // Build grid from queries and sizes
                // Filter out empty queries
                const validQueries: string[] = [];
                const validSizes: number[] = [];
                for (let i = 0; i < sqlQueries.length && i < sizes.length; i++) {
                    if (sqlQueries[i]?.trim() && sizes[i] > 0) {
                        validQueries.push(sqlQueries[i]);
                        validSizes.push(sizes[i]);
                    }
                }

                // Initialize grid cells
                const newCellQueries: string[][] = Array.from({ length: finalRows }, () =>
                    Array.from({ length: finalCols }, () => '')
                );
                const newCellSpans: { rowSpan: number; colSpan: number }[][] = Array.from({ length: finalRows }, () =>
                    Array.from({ length: finalCols }, () => ({ rowSpan: 1, colSpan: 1 }))
                );

                // Place queries in grid sequentially
                // Track which cells are covered by spans
                const covered: boolean[][] = Array.from({ length: finalRows }, () =>
                    Array.from({ length: finalCols }, () => false)
                );

                let currentRow = 0;
                let currentCol = 0;
                for (let i = 0; i < validQueries.length; i++) {
                    // Find next available position
                    while (currentRow < finalRows && covered[currentRow][currentCol]) {
                        currentCol++;
                        if (currentCol >= finalCols) {
                            currentCol = 0;
                            currentRow++;
                        }
                    }

                    if (currentRow >= finalRows) break;

                    // Parse size to get rowSpan and colSpan (e.g., 21 -> 2 rows, 1 col)
                    const sizeStr = validSizes[i].toString();
                    const rowSpan = sizeStr.length >= 2 ? parseInt(sizeStr[0]) || 1 : 1;
                    const colSpan = sizeStr.length >= 2 ? parseInt(sizeStr[1]) || 1 : 1;

                    const finalRowSpan = Math.min(rowSpan, finalRows - currentRow);
                    const finalColSpan = Math.min(colSpan, finalCols - currentCol);

                    // Set cell query and span
                    newCellQueries[currentRow][currentCol] = validQueries[i];
                    newCellSpans[currentRow][currentCol] = {
                        rowSpan: finalRowSpan,
                        colSpan: finalColSpan
                    };

                    // Mark covered cells
                    for (let r = currentRow; r < currentRow + finalRowSpan && r < finalRows; r++) {
                        for (let c = currentCol; c < currentCol + finalColSpan && c < finalCols; c++) {
                            covered[r][c] = true;
                        }
                    }

                    // Move to next position
                    currentCol += finalColSpan;
                    if (currentCol >= finalCols) {
                        currentCol = 0;
                        currentRow++;
                    }
                }

                setCellQueries(newCellQueries);
                setCellSpans(newCellSpans);

                hasInitialized.current = true;
                lastModelEmptyState.current = false;
            } else if (model.sqlQueries && model.sqlQueries.length > 0 && model.sqlQueries.some(q => q?.trim())) {
                // Normal mode: initialize from model (already handled by edit mode logic above)
                hasInitialized.current = true;
                lastModelEmptyState.current = false;
            } else {
                hasInitialized.current = true;
                lastModelEmptyState.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialValues, props.editingJobID]);

    // Set dashboard title when initialValues changes (for edit mode)
    useEffect(() => {
        if (props.initialValues && props.editingJobID && props.initialValues.title) {
            setDashboardTitle(props.initialValues.title);
        }
    }, [props.initialValues, props.editingJobID]);

    // Watch for model being cleared (when clicking "New Dashboard")
    useEffect(() => {
        if (hasInitialized.current) {
            const modelIsEmpty = !model.sqlQueries || model.sqlQueries.length === 0 || !model.sqlQueries.some(q => q?.trim());
            const wasEmpty = lastModelEmptyState.current;

            // Only clear form if model went from having content to empty (explicit clear)
            // BUT don't clear if we're in edit mode
            if (!wasEmpty && modelIsEmpty && !props.editingJobID) {
                setDashboardTitle('');
                // Reset grid to 1x1
                setGridRows(1);
                setGridCols(1);
                // Clear cell queries and spans
                setCellQueries([['']]);
                setCellSpans([[{ rowSpan: 1, colSpan: 1 }]]);
            }

            lastModelEmptyState.current = modelIsEmpty;
        }
    }, [model.sqlQueries, props.editingJobID]);


    const validateDashboardTitle = (title: string): boolean => {
        const trimmed = title.trim();

        // Check if title is empty
        if (!trimmed || trimmed.length === 0) {
            notification.error({
                message: 'Invalid dashboard title',
                description: 'Dashboard title cannot be empty.',
                placement: 'bottomLeft',
                duration: 3
            });
            return false;
        }

        // Validate: only alphanumeric and specific special characters (including spaces)
        const allowedPattern = /^[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]+$/;
        if (!allowedPattern.test(trimmed)) {
            // Find invalid characters
            const invalidChars = new Set<string>();
            for (const char of trimmed) {
                if (!/[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]/.test(char)) {
                    invalidChars.add(char === ' ' ? '(space)' : char);
                }
            }
            const invalidCharsList = Array.from(invalidChars).join(', ');

            // Invalid characters - show error notification
            notification.error({
                message: 'Invalid dashboard title',
                description: `Invalid characters: ${invalidCharsList}`,
                placement: 'bottomLeft',
                duration: 3
            });
            return false;
        }

        return true;
    };

    // Build flattened queries/sizes from the grid + spans.
    // Each "origin" cell (not covered by another cell's span) becomes one tile.
    const buildGridQueriesAndSizes = (): { allFilled: boolean; queries: string[]; sizes: number[] } => {
        const queries: string[] = [];
        const sizes: number[] = [];

        // Compute which grid positions are covered by spans originating elsewhere
        const covered: boolean[][] = Array.from({ length: gridRows }, () =>
            Array.from({ length: gridCols }, () => false)
        );
        for (let r = 0; r < gridRows; r++) {
            for (let c = 0; c < gridCols; c++) {
                const span = cellSpans[r]?.[c] ?? { rowSpan: 1, colSpan: 1 };
                for (let rr = r; rr < r + span.rowSpan && rr < gridRows; rr++) {
                    for (let cc = c; cc < c + span.colSpan && cc < gridCols; cc++) {
                        if (!(rr === r && cc === c)) {
                            covered[rr][cc] = true;
                        }
                    }
                }
            }
        }

        for (let r = 0; r < gridRows; r++) {
            for (let c = 0; c < gridCols; c++) {
                if (covered[r][c]) continue;
                const raw = (cellQueries[r]?.[c] ?? '').trim();
                if (!raw) {
                    return { allFilled: false, queries: [], sizes: [] };
                }

                // If the value matches a saved query name, use its SQL; otherwise treat as manual SQL.
                const fromSaved = Object.prototype.hasOwnProperty.call(savedQueries || {}, raw)
                    ? (savedQueries as Record<string, string>)[raw]
                    : raw;

                const span = cellSpans[r]?.[c] ?? { rowSpan: 1, colSpan: 1 };
                const rowCount = Math.max(1, span.rowSpan);
                const colCount = Math.max(1, span.colSpan);
                // Size is concatenation of rows and cols, e.g. 2 rows, 1 col => 21
                const sizeNum = parseInt(`${rowCount}${colCount}`) || 11;

                queries.push(fromSaved);
                sizes.push(sizeNum);
            }
        }

        return { allFilled: true, queries, sizes };
    };

    const handleSubmit = async () => {
        const trimmedTitle = dashboardTitle.trim();

        // Validate dashboard title
        if (!validateDashboardTitle(trimmedTitle)) {
            return;
        }

        const { allFilled, queries, sizes } = buildGridQueriesAndSizes();
        if (!allFilled || queries.length === 0) {
            return;
        }

        // Overall dashboard dimensions as rows/cols concat (e.g. 34 for 3 rows x 4 cols)
        const dimensions = parseInt(`${gridRows}${gridCols}`) || 0;

        // Pad arrays to 24 elements (API expects up to 24)
        const validQueries = [...queries];
        const validSizes = [...sizes];
        while (validQueries.length < 24) {
            validQueries.push('');
        }
        while (validSizes.length < 24) {
            validSizes.push(0);
        }

        if (props.editingJobID) {
            // Edit mode: regenerate the job
            try {
                await model.regenerate(props.editingJobID, validQueries, validSizes, trimmedTitle, dimensions);
            } catch (error) {
                console.error("Failed to regenerate dashboard:", error);
                notification.error({
                    message: 'Failed to regenerate dashboard',
                    description: 'Please try again.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            }
        } else {
            // Normal mode: create new job
            model.submit(validQueries, validSizes, trimmedTitle, dimensions);
        }
    };

    const isTitleValid = (): boolean => {
        const trimmed = dashboardTitle.trim();
        if (!trimmed || trimmed.length === 0) {
            return false;
        }
        const allowedPattern = /^[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]+$/;
        return allowedPattern.test(trimmed);
    };

    // Enable button only when title is valid and every tile has content.
    // Always disabled in admin "view as" mode (the backend rejects the
    // mutation; this just prevents the wasted click).
    const isButtonEnabled = (() => {
        if (isReadOnly) return false;
        if (!(model.canSubmit && !isAnimating && isTitleValid())) {
            return false;
        }
        const { allFilled, queries } = buildGridQueriesAndSizes();
        return allFilled && queries.length > 0;
    })();

    return (
        <>
            <style>{`
                .sql-input-textarea::-webkit-scrollbar {
                    width: 8px;
                }
                .sql-input-textarea::-webkit-scrollbar-track {
                    background: transparent;
                }
                .sql-input-textarea::-webkit-scrollbar-thumb {
                    background: ${darkMode ? themeColors.borderColorSecondary : "#888"};
                    border-radius: 4px;
                    border: 2px solid transparent;
                    background-clip: padding-box;
                }
                .sql-input-textarea::-webkit-scrollbar-thumb:hover {
                    background: ${themeColors.borderColor};
                    background-clip: padding-box;
                }
                .cell-editor-modal .ant-modal-close,
                .cell-editor-modal .ant-modal-close-x {
                    color: ${themeColors.textColor} !important;
                }
                .cell-editor-modal .ant-modal-close:hover,
                .cell-editor-modal .ant-modal-close-x:hover {
                    color: ${themeColors.textColor} !important;
                }
                ${darkMode ? `
                    .row-menu-dropdown .ant-dropdown-menu {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item-disabled {
                        color: ${themeColors.textColorMuted} !important;
                        opacity: 0.5 !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item-divider {
                        background-color: ${themeColors.borderColor} !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item-icon {
                        color: ${themeColors.textColor} !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item-danger {
                        color: #ff4d4f !important;
                    }
                    .row-menu-dropdown .ant-dropdown-menu-item-danger:hover {
                        background-color: #ff4d4f !important;
                        color: white !important;
                    }
                    .saved-query-select {
                        height: 100% !important;
                    }
                    .saved-query-select .ant-select-selector {
                        background-color: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        height: 100% !important;
                        padding: 0.5em !important;
                    }
                    .saved-query-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .saved-query-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .saved-query-select .ant-select-arrow {
                        display: none !important;
                    }
                    .saved-query-select .ant-select-clear {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .saved-query-select .ant-select-clear:hover {
                        color: ${themeColors.textColor} !important;
                    }
                    .saved-query-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                    }
                    .saved-query-select-dropdown .ant-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .saved-query-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .saved-query-select-dropdown .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .saved-query-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColorSecondary} !important;
                        box-shadow: none !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .cell-editor-saved-select .ant-select-clear {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .cell-editor-saved-select .ant-select-clear:hover {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item:hover,
                    .cell-editor-saved-select-dropdown .ant-select-item-option-active {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-textarea textarea::placeholder,
                    .cell-editor-textarea::placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                ` : `
                    .saved-query-select {
                        height: 100% !important;
                    }
                    .saved-query-select .ant-select-selector {
                        background-color: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        height: 100% !important;
                        padding: 0.5em !important;
                    }
                    .saved-query-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .saved-query-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .saved-query-select .ant-select-arrow {
                        display: none !important;
                    }
                    .saved-query-select .ant-select-clear {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .saved-query-select .ant-select-clear:hover {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColor} !important;
                        border-color: ${themeColors.borderColorSecondary} !important;
                        box-shadow: none !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .cell-editor-saved-select .ant-select-clear {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .cell-editor-saved-select .ant-select-clear:hover {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select-dropdown {
                        background-color: ${themeColors.backgroundColor} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item:hover,
                    .cell-editor-saved-select-dropdown .ant-select-item-option-active {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .cell-editor-saved-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .cell-editor-textarea textarea::placeholder,
                    .cell-editor-textarea::placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                `}
            `}</style>
            <div style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gridTemplateRows: "auto 1fr auto",
                width: "100%",
                height: "100%",
                border: `1px solid ${themeColors.borderColor}`,
                backgroundColor: darkMode ? "#1f1f1f" : "#ffffff"
            }}>
                {/* Dashboard Title Input */}
                <div style={{
                    gridColumn: "1 / -1",
                    borderBottom: `1px solid ${themeColors.borderColor}`,
                    padding: "0.5em",
                    display: "flex",
                    alignItems: "center"
                }}>
                    <input
                        type="text"
                        value={dashboardTitle}
                        onChange={(e) => setDashboardTitle(e.target.value)}
                        disabled={model.isSubmitting}
                        placeholder="Dashboard Title"
                        style={{
                            width: "100%",
                            padding: "0.5em",
                            border: "none",
                            borderRadius: "0",
                            backgroundColor: "transparent",
                            color: themeColors.textColor,
                            fontSize: "1em",
                            outline: "none"
                        }}
                    />
                </div>

                {/* Grid layout builder */}
                <div style={{
                    gridColumn: "1 / -1",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    padding: 0,
                    minHeight: 0
                }}>
                    <div style={{
                        flex: 1,
                        border: `1px dashed ${themeColors.borderColor}`,
                        borderRadius: "0",
                        backgroundColor: darkMode ? "#141414" : "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        justifyContent: "space-between",
                        minHeight: 0
                    }}>
                        {/* Remove row strip (above grid) */}
                            <div
                            onClick={() => {
                                if (!model.isSubmitting && gridRows > 1) {
                                    setGridRows(r => Math.max(1, r - 1));
                                }
                            }}
                            style={{
                                padding: "0.25em 0.75em",
                                paddingTop: "0.5em",
                                paddingBottom: "0",
                                fontSize: "0.85em",
                                color: gridRows <= 1 || model.isSubmitting ? themeColors.textColorMuted : themeColors.textColorSecondary,
                                cursor: gridRows <= 1 || model.isSubmitting ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                userSelect: "none"
                            }}
                        >
                            Remove row
                        </div>

                        {/* Grid viewport with left/right column controls */}
                        <div style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "stretch",
                            justifyContent: "stretch",
                            padding: "0.25em 0.5em 0.5em 0.5em",
                            minHeight: 0
                        }}>
                            {/* Remove column strip (left) */}
                            <div
                                onClick={() => {
                                    if (!model.isSubmitting && gridCols > 1) {
                                        setGridCols(c => Math.max(1, c - 1));
                                    }
                                }}
                                style={{
                                    width: "1.75em",
                                    fontSize: "0.8em",
                                    color: gridCols <= 1 || model.isSubmitting ? themeColors.textColorMuted : themeColors.textColorSecondary,
                                cursor: gridCols <= 1 || model.isSubmitting ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    userSelect: "none",
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed",
                                    transform: "rotate(180deg)"
                                }}
                            >
                                Remove col
                            </div>

                            <div style={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                overflow: "hidden"
                            }}>
                                {(() => {
                                    // Mark cells that are covered by a span originating elsewhere
                                    const covered: boolean[][] = Array.from({ length: gridRows }, () =>
                                        Array.from({ length: gridCols }, () => false)
                                    );
                                    for (let r = 0; r < gridRows; r++) {
                                        for (let c = 0; c < gridCols; c++) {
                                            const span = cellSpans[r]?.[c] ?? { rowSpan: 1, colSpan: 1 };
                                            for (let rr = r; rr < r + span.rowSpan && rr < gridRows; rr++) {
                                                for (let cc = c; cc < c + span.colSpan && cc < gridCols; cc++) {
                                                    if (!(rr === r && cc === c)) {
                                                        covered[rr][cc] = true;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    return (
                                        <div style={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "grid",
                                            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                                            gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                                            gap: "6px",
                                            padding: "4px"
                                        }}>
                                            {Array.from({ length: gridRows * gridCols }).map((_, i) => {
                                                const row = Math.floor(i / gridCols);
                                                const col = i % gridCols;
                                                if (covered[row][col]) {
                                                    return null;
                                                }
                                                const value = cellQueries[row]?.[col] ?? '';
                                                const isSavedQueryName = !!value && Object.prototype.hasOwnProperty.call(savedQueries || {}, value);
                                                const span = cellSpans[row]?.[col] ?? { rowSpan: 1, colSpan: 1 };
                                                return (
                                                    <div
                                                        key={`${row}-${col}`}
                                                        onClick={() => {
                                                            if (!model.isSubmitting) {
                                                        const existing = value;
                                                        const entries = Object.entries(savedQueries as Record<string, any>);
                                                        const match = entries.find(([name]) => name === existing);
                                                        if (match) {
                                                            // Existing value is a saved query name
                                                            setCellMode('saved');
                                                            setCellSelectedName(match[0]);
                                                            setCellText('');
                                                        } else if (existing && existing.trim().length > 0) {
                                                            // Existing value is manual SQL
                                                            setCellMode('manual');
                                                            setCellSelectedName(undefined);
                                                            setCellText(existing);
                                                        } else {
                                                            // Empty cell: default to Manual tab
                                                            setCellMode('manual');
                                                            setCellSelectedName(undefined);
                                                            setCellText('');
                                                        }
                                                                setActiveCell({ row, col });
                                                                setIsCellEditorOpen(true);
                                                            }
                                                        }}
                                                        style={{
                                                            gridRow: `${row + 1} / span ${span.rowSpan}`,
                                                            gridColumn: `${col + 1} / span ${span.colSpan}`,
                                                            borderRadius: "6px",
                                                            border: isSavedQueryName
                                                                ? `1px solid ${themeColors.primaryColor}`
                                                                : `1px solid ${themeColors.borderColorSecondary}`,
                                                            background: isSavedQueryName
                                                                ? (
                                                                    darkMode
                                                                        ? "linear-gradient(135deg, rgba(120, 188, 255, 0.12), rgba(120, 188, 255, 0.06))"
                                                                        : "linear-gradient(135deg, rgba(24, 144, 255, 0.12), rgba(24, 144, 255, 0.04))"
                                                                )
                                                                : (
                                                                    darkMode
                                                                        ? "linear-gradient(145deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
                                                                        : "linear-gradient(145deg, rgba(0,0,0,0.02), rgba(0,0,0,0.01))"
                                                                ),
                                                            boxShadow: isSavedQueryName
                                                                ? "0 0 0 1px rgba(24, 144, 255, 0.3)"
                                                                : (
                                                                    darkMode
                                                                        ? "inset 0 0 0 1px rgba(255,255,255,0.02)"
                                                                        : "inset 0 0 0 1px rgba(0,0,0,0.02)"
                                                                ),
                                                            cursor: model.isSubmitting ? "not-allowed" : "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            padding: "0.25em",
                                                            overflow: "auto"
                                                        }}
                                                    >
                                                        {value && (
                                                            <span
                                                                style={{
                                                                    fontSize: "0.75em",
                                                                    fontWeight: isSavedQueryName ? 600 : 400,
                                                                    color: isSavedQueryName ? themeColors.textColor : themeColors.textColorSecondary,
                                                                    whiteSpace: "pre",
                                                                    maxWidth: "100%"
                                                                }}
                                                            >
                                                                {value}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Add column strip (right) */}
                            <div
                                onClick={() => {
                                    if (!model.isSubmitting && (gridRows * (gridCols + 1)) <= 24) {
                                        setGridCols(c => c + 1);
                                    }
                                }}
                                style={{
                                    width: "1.75em",
                                    fontSize: "0.8em",
                                    color: (gridRows * (gridCols + 1)) > 24 || model.isSubmitting ? themeColors.textColorMuted : themeColors.textColorSecondary,
                                    cursor: (gridRows * (gridCols + 1)) > 24 || model.isSubmitting ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    userSelect: "none",
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed"
                                }}
                            >
                                Add col
                            </div>
                        </div>

                        {/* Add row strip (below grid) */}
                        <div style={{
                            padding: "0.25em 0.75em",
                            paddingTop: "0",
                            paddingBottom: "0.5em",
                            fontSize: "0.85em",
                            color: ((gridRows + 1) * gridCols) > 24 || model.isSubmitting ? themeColors.textColorMuted : themeColors.textColorSecondary,
                            cursor: ((gridRows + 1) * gridCols) > 24 || model.isSubmitting ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            userSelect: "none"
                        }}>
                            <span
                                onClick={() => {
                                    if (!model.isSubmitting && ((gridRows + 1) * gridCols) <= 24) {
                                        setGridRows(r => r + 1);
                                    }
                                }}
                            >
                                Add row
                            </span>
                        </div>
                    </div>
                </div>

                {/* Submit Button */}
                <div style={{
                    gridColumn: "1 / -1",
                    borderTop: `1px solid ${themeColors.borderColor}`,
                    padding: "0",
                    backgroundColor: darkMode ? "#1f1f1f" : "#ffffff"
                }}>
                    <button
                        onClick={handleSubmit}
                        disabled={!isButtonEnabled}
                        style={{
                            width: "100%",
                            padding: "1em 1.5em",
                            border: "none",
                            backgroundColor: isButtonEnabled
                                ? themeColors.primaryColor
                                : themeColors.borderColorSecondary,
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.75em",
                            cursor: isButtonEnabled ? "pointer" : "not-allowed",
                            transition: "all 0.2s ease",
                            fontSize: "1em",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            opacity: isButtonEnabled ? 1 : 0.6
                        }}
                        onMouseEnter={(e) => {
                            if (isButtonEnabled) {
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = `0 4px 12px rgba(24, 144, 255, 0.4)`;
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isButtonEnabled) {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }
                        }}
                    >
                        <ArrowUpOutlined style={{ fontSize: "1.1em" }} />
                        <span>{props.editingJobID ? "REGENERATE DASHBOARD" : "Generate Dashboard"}</span>
                    </button>
                </div>
            </div>
            <Modal
                rootClassName="cell-editor-modal"
                title={<span style={{ color: themeColors.textColor }}>{cellMode === 'saved' ? "Select saved query" : "Custom query"}</span>}
                open={isCellEditorOpen}
                onCancel={() => setIsCellEditorOpen(false)}
                footer={null}
                centered
                width={1400}
                styles={{
                    header: {
                        backgroundColor: darkMode ? themeColors.backgroundColorSecondary : "#ffffff",
                        color: themeColors.textColor,
                        borderBottom: `1px solid ${themeColors.borderColor}`
                    },
                    body: {
                        padding: "1em 1.5em",
                        backgroundColor: darkMode ? themeColors.backgroundColorSecondary : "#ffffff",
                        color: themeColors.textColor
                    },
                    content: {
                        backgroundColor: darkMode ? themeColors.backgroundColorSecondary : "#ffffff",
                        border: `1px solid ${themeColors.borderColor}`
                    }
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75em" }}>
                    <div style={{ display: "flex", gap: "0.75em", marginBottom: "0.5em" }}>
                        <button
                            type="button"
                            onClick={() => setCellMode('manual')}
                            style={{
                                flex: 1,
                                padding: "0.4em 0.75em",
                                borderRadius: "6px",
                                border: cellMode === 'manual'
                                    ? `1px solid ${themeColors.primaryColor}`
                                    : `1px solid ${themeColors.borderColorSecondary}`,
                                backgroundColor: cellMode === 'manual'
                                    ? themeColors.primaryColor
                                    : "transparent",
                                color: cellMode === 'manual' ? "#ffffff" : themeColors.textColor,
                                cursor: "pointer",
                                fontSize: "0.9em"
                            }}
                        >
                            Manual
                        </button>
                        <button
                            type="button"
                            onClick={() => setCellMode('saved')}
                            style={{
                                flex: 1,
                                padding: "0.4em 0.75em",
                                borderRadius: "6px",
                                border: cellMode === 'saved'
                                    ? `1px solid ${themeColors.primaryColor}`
                                    : `1px solid ${themeColors.borderColorSecondary}`,
                                backgroundColor: cellMode === 'saved'
                                    ? themeColors.primaryColor
                                    : "transparent",
                                color: cellMode === 'saved' ? "#ffffff" : themeColors.textColor,
                                cursor: "pointer",
                                fontSize: "0.9em"
                            }}
                        >
                            Saved query
                        </button>
                    </div>

                    {cellMode === 'saved' ? (
                        <Select
                            value={cellSelectedName}
                            onChange={(value) => {
                                setCellSelectedName(value);
                            }}
                            placeholder="Select saved query"
                            options={Object.keys(savedQueries as Record<string, any>).sort().map(name => ({
                                value: name,
                                label: name
                            }))}
                            style={{
                                width: "100%"
                            }}
                            className="cell-editor-saved-select"
                            popupClassName="cell-editor-saved-select-dropdown"
                        />
                    ) : (
                        <TextArea
                            className="cell-editor-textarea"
                            value={cellText}
                            onChange={e => setCellText(e.target.value)}
                            placeholder="Enter SQL for this cell..."
                            autoSize={{ minRows: 5, maxRows: 15 }}
                            style={{
                                width: "100%",
                                resize: "vertical",
                                backgroundColor: darkMode ? themeColors.backgroundColorSecondary : "#ffffff",
                                color: themeColors.textColor,
                                borderColor: themeColors.borderColorSecondary
                            }}
                        />
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75em", marginTop: "0.5em" }}>
                        {activeCell && (
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75em" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
                                    <span style={{ fontSize: "0.8em", color: themeColors.textColorSecondary }}>Row span</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const { row, col } = activeCell;
                                            const current = cellSpans[row]?.[col] ?? { rowSpan: 1, colSpan: 1 };
                                            if (current.rowSpan > 1) {
                                                setCellSpans(prev => {
                                                    const next = prev.map(rowArr => rowArr.map(span => ({ ...span })));
                                                    next[row][col].rowSpan = current.rowSpan - 1;
                                                    return next;
                                                });
                                            }
                                        }}
                                        style={{
                                            border: "none",
                                            background: "none",
                                            color: themeColors.textColor,
                                            cursor: "pointer",
                                            fontSize: "0.85em"
                                        }}
                                    >
                                        −
                                    </button>
                                    <span style={{ fontSize: "0.85em", color: themeColors.textColor }}>
                                        {(cellSpans[activeCell.row]?.[activeCell.col]?.rowSpan) ?? 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const { row, col } = activeCell;
                                            const maxRowSpan = gridRows - row;
                                            const current = cellSpans[row]?.[col] ?? { rowSpan: 1, colSpan: 1 };
                                            if (current.rowSpan < maxRowSpan) {
                                                setCellSpans(prev => {
                                                    const next = prev.map(rowArr => rowArr.map(span => ({ ...span })));
                                                    next[row][col].rowSpan = current.rowSpan + 1;
                                                    return next;
                                                });
                                            }
                                        }}
                                        style={{
                                            border: "none",
                                            background: "none",
                                            color: themeColors.textColor,
                                            cursor: "pointer",
                                            fontSize: "0.85em"
                                        }}
                                    >
                                        +
                                    </button>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
                                    <span style={{ fontSize: "0.8em", color: themeColors.textColorSecondary }}>Col span</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const { row, col } = activeCell;
                                            const current = cellSpans[row]?.[col] ?? { rowSpan: 1, colSpan: 1 };
                                            if (current.colSpan > 1) {
                                                setCellSpans(prev => {
                                                    const next = prev.map(rowArr => rowArr.map(span => ({ ...span })));
                                                    next[row][col].colSpan = current.colSpan - 1;
                                                    return next;
                                                });
                                            }
                                        }}
                                        style={{
                                            border: "none",
                                            background: "none",
                                            color: themeColors.textColor,
                                            cursor: "pointer",
                                            fontSize: "0.85em"
                                        }}
                                    >
                                        −
                                    </button>
                                    <span style={{ fontSize: "0.85em", color: themeColors.textColor }}>
                                        {(cellSpans[activeCell.row]?.[activeCell.col]?.colSpan) ?? 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const { row, col } = activeCell;
                                            const maxColSpan = gridCols - col;
                                            const current = cellSpans[row]?.[col] ?? { rowSpan: 1, colSpan: 1 };
                                            if (current.colSpan < maxColSpan) {
                                                setCellSpans(prev => {
                                                    const next = prev.map(rowArr => rowArr.map(span => ({ ...span })));
                                                    next[row][col].colSpan = current.colSpan + 1;
                                                    return next;
                                                });
                                            }
                                        }}
                                        style={{
                                            border: "none",
                                            background: "none",
                                            color: themeColors.textColor,
                                            cursor: "pointer",
                                            fontSize: "0.85em"
                                        }}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={{ display: "flex", gap: "0.75em", marginTop: "0.25em" }}>
                            {activeCell && !!(cellQueries[activeCell.row]?.[activeCell.col] ?? '').trim() && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (activeCell) {
                                            const next = cellQueries.map(row => [...row]);
                                            next[activeCell.row][activeCell.col] = '';
                                            setCellQueries(next);
                                        }
                                        setIsCellEditorOpen(false);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: "0.6em 0.75em",
                                        borderRadius: "6px",
                                        border: `1px solid ${themeColors.borderColorSecondary}`,
                                        backgroundColor: "transparent",
                                        color: themeColors.textColorSecondary,
                                        cursor: "pointer",
                                        fontSize: "0.85em",
                                        fontWeight: 500
                                    }}
                                >
                                    Clear cell
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeCell) {
                                        const next = cellQueries.map(row => [...row]);
                                        if (cellMode === 'saved' && cellSelectedName) {
                                            // Store the saved query *name* in the cell
                                            next[activeCell.row][activeCell.col] = cellSelectedName;
                                        } else if (cellMode === 'manual') {
                                            next[activeCell.row][activeCell.col] = cellText;
                                        }
                                        setCellQueries(next);
                                    }
                                    setIsCellEditorOpen(false);
                                }}
                                style={{
                                    flex: 1,
                                    padding: "0.6em 0.75em",
                                    borderRadius: "6px",
                                    border: "none",
                                    backgroundColor: themeColors.primaryColor,
                                    color: "#ffffff",
                                    cursor: "pointer",
                                    fontSize: "0.9em",
                                    fontWeight: 500
                                }}
                            >
                                Apply to cell
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}




